import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Sheet, type IconName } from '@astral/ui';
import { LANGUAGE_META } from '@astral/shared';
import { openTelegramLink } from '@astral/telegram/webapp';
import { useAuth, useCurrentUser } from '../providers/AuthProvider.js';
import { useI18n, useT, type TranslationKey } from '../providers/I18nProvider.js';
import { ConstellationMark } from '../components/ConstellationMark.js';
import { Sparkles } from '../components/Sparkles.js';
import { CelestialHero } from '../components/CelestialHero.js';
import { SectionOrnament } from '../components/SectionOrnament.js';
import { PerimeterGlint } from '../components/PerimeterGlint.js';
import { AstralArt } from '../components/AstralArt.js';
import { LanguagePicker } from '../components/LanguagePicker.js';
import { EmojiPresetManager } from '../components/EmojiPresetManager.js';

interface Tool {
  key: string;
  icon: IconName;
  /** Navigation target, for tools that own a screen. */
  route?: string;
  /** In-place action, for tools whose UI is a sheet (emoji presets). */
  action?: 'emojis';
}

/** The six primary user tools, in reference order (2 columns × 3 rows). */
const TOOLS: Tool[] = [
  { key: 'projects', icon: 'folder', route: '/projects' },
  { key: 'premium', icon: 'crown', route: '/premium' },
  { key: 'emojis', icon: 'smile', action: 'emojis' },
  { key: 'channels', icon: 'bookmark', route: '/channels' },
  { key: 'settings', icon: 'settings', route: '/settings' },
  { key: 'guide', icon: 'help', route: '/guide' },
];

const INTRO_KEY = 'astral.introSeen';
const OFFICIAL_CHANNEL = 'https://t.me/AstralAtelier';

/** The celestial hub: identity, luminous moon hero, create CTA, tools, channel. */
export function HomeScreen(): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const navigate = useNavigate();
  const { hasPermission, updatePreferences } = useAuth();
  const user = useCurrentUser();

  const [langSheet, setLangSheet] = useState(false);
  const [emojiSheet, setEmojiSheet] = useState(false);
  const [intro, setIntro] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(INTRO_KEY)) setIntro(true);
    } catch {
      /* storage unavailable — skip intro */
    }
  }, []);
  const dismissIntro = (): void => {
    setIntro(false);
    try {
      localStorage.setItem(INTRO_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const openTool = (tool: Tool): void => {
    if (tool.action === 'emojis') setEmojiSheet(true);
    else if (tool.route) navigate(tool.route);
  };

  return (
    <div className="screen home">
      {/* Stardust/sparkles are one page-level layer now (see App) — not per screen. */}

      {/* A. Compact identity row + small language pill */}
      <header className="home__identity">
        <div className="home__identity-main">
          {user.photoUrl ? (
            <span className="home__avatar">
              <img src={user.photoUrl} alt="" referrerPolicy="no-referrer" />
            </span>
          ) : (
            // Standalone container with explicit dimensions — deliberately NOT
            // the circular `.home__avatar` frame, whose rounded box-shadow and
            // `overflow: hidden` produced the boxy glow, and whose `width: auto`
            // let the artwork collapse to zero on iOS.
            <span className="identity-mark">
              <AstralArt name="moon" halo="steady" alt="Astral" />
            </span>
          )}
          <div className="home__identity-text">
            <span className="home__identity-name metallic-text">{user.firstName || 'Astral'}</span>
            <span className="home__identity-sub">
              {user.username ? `@${user.username}` : 'Astral Studio'}
            </span>
          </div>
        </div>
        <button className="lang-pill" onClick={() => setLangSheet(true)} aria-label={t('settings.language')}>
          <Icon name="globe" size={14} />
          <span className="lang-pill__label">{LANGUAGE_META[language].nativeName}</span>
          <Icon name="chevron-down" size={13} />
        </button>
      </header>

      {/* B. Celestial hero — luminous moon in drifting nebula */}
      <CelestialHero />

      {/* C. Primary Create CTA */}
      <button className="create-card" onClick={() => navigate('/projects/new')}>
        <PerimeterGlint radius={24} />
        <span className="create-card__icon">
          <Icon name="sparkle-4" size={22} />
        </span>
        <span className="create-card__text">
          <span className="create-card__title">{t('home.createPack')}</span>
          <span className="create-card__sub">{t('home.createPackSub')}</span>
        </span>
        <Icon name="chevron-right" size={20} className="chev" />
      </button>

      {/* D. Astral Tools — ornamented heading + the primary six */}
      <section className="stack" style={{ gap: 10 }}>
        <div className="section-head">
          <span className="section-head__title">{t('home.toolsTitle')}</span>
          <SectionOrnament />
        </div>

        <div className="home__tools">
          {TOOLS.map((tool) => (
            <button key={tool.key} className="tool-card" onClick={() => openTool(tool)}>
              <span className="tool-card__icon">
                <Icon name={tool.icon} size={19} />
              </span>
              <span className="tool-card__text">
                <span className="tool-card__title">{t(`home.tools.${tool.key}` as TranslationKey)}</span>
                <span className="tool-card__sub">{t(`home.tools.${tool.key}Sub` as TranslationKey)}</span>
              </span>
              <Icon name="chevron-right" size={16} className="chev tool-card__chev" />
            </button>
          ))}
        </div>

        {/* Staff-only entry — deliberately outside the primary six. */}
        {hasPermission('admin:access') && (
          <button className="admin-entry" onClick={() => navigate('/admin')}>
            <Icon name="shield" size={15} />
            <span className="admin-entry__label">{t('home.tools.admin')}</span>
            <Icon name="chevron-right" size={14} className="chev" />
          </button>
        )}
      </section>

      {/* E. Official Astral channel — one cohesive tappable card */}
      <button
        className="official-card"
        aria-label={t('channels.open')}
        onClick={() => openTelegramLink(OFFICIAL_CHANNEL)}
      >
        <span className="official-card__icon">
          <Icon name="telegram" size={20} />
        </span>
        <span className="official-card__text">
          <span className="official-card__name">{t('home.officialName')}</span>
          <span className="official-card__sub">{t('home.officialSub')}</span>
        </span>
        <Icon name="chevron-right" size={18} className="chev" />
      </button>

      {/* Language sheet */}
      <Sheet open={langSheet} onClose={() => setLangSheet(false)} title={t('settings.language')}>
        <LanguagePicker
          value={language}
          onSelect={(lang) => {
            void updatePreferences({ language: lang });
            setLangSheet(false);
          }}
        />
      </Sheet>

      {/* Emoji presets — the existing server-backed manager */}
      <EmojiPresetManager open={emojiSheet} onClose={() => setEmojiSheet(false)} />

      {/* First-launch intro */}
      <Sheet open={intro} onClose={dismissIntro} title="">
        <div className="intro-sheet">
          <Sparkles count={8} />
          {/* Constellation, not a second crescent — the hero already has one. */}
          <ConstellationMark />
          <h2 className="intro-sheet__title metallic-text">{t('home.introTitle')}</h2>
          <p className="intro-sheet__body">{t('home.introBody')}</p>
          <Button size="lg" fullWidth leftIcon={<Icon name="sparkles" size={18} />} onClick={dismissIntro}>
            {t('home.introCta')}
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
